(() => {
  const focusableSelector = 'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]';
  const regionSelector = '.flickityt4s-slider > [aria-hidden], .t4s-drawer[aria-hidden], .t4s-content-login-sidebar[aria-hidden]';
  const supportsInert = 'inert' in HTMLElement.prototype;

  const matchesWithin = (root, selector) => {
    if (!root) return [];
    const matches = [];
    if (root.nodeType === 1 && root.matches(selector)) matches.push(root);
    if (root.querySelectorAll) root.querySelectorAll(selector).forEach((item) => matches.push(item));
    return matches;
  };

  const syncFocusableDescendants = (region, hidden) => {
    region.querySelectorAll(focusableSelector).forEach((control) => {
      if (hidden) {
        if (!control.hasAttribute('data-a11y-prev-tabindex')) {
          const previous = control.getAttribute('tabindex');
          control.setAttribute('data-a11y-prev-tabindex', previous === null ? '__none__' : previous);
        }
        control.setAttribute('tabindex', '-1');
      } else if (control.hasAttribute('data-a11y-prev-tabindex')) {
        const previous = control.getAttribute('data-a11y-prev-tabindex');
        if (previous === '__none__') control.removeAttribute('tabindex');
        else control.setAttribute('tabindex', previous);
        control.removeAttribute('data-a11y-prev-tabindex');
      }
    });
  };

  const syncRegion = (region) => {
    if (!region || !region.matches(regionSelector)) return;
    const hidden = region.getAttribute('aria-hidden') === 'true';
    if (hidden) region.setAttribute('inert', '');
    else region.removeAttribute('inert');
    if (!supportsInert) syncFocusableDescendants(region, hidden);
  };

  const setHiddenRegionState = (root = document) => {
    matchesWithin(root, regionSelector).forEach(syncRegion);
  };

  const labelDynamicControls = (root = document) => {
    const selector = [
      'a.t4s-full-width-link:not([aria-label])',
      '.t4s-quantity-selector.is--minus:not([aria-label])',
      '.t4s-quantity-selector.is--plus:not([aria-label])',
      '.t4s-quantity-input:not([aria-label])',
      'iframe[src*="merchantverse"]:not([title])',
      'iframe[src*="preview-bar"]:not([title])'
    ].join(',');

    matchesWithin(root, selector).forEach((control) => {
      if (control.matches('a.t4s-full-width-link')) {
        const card = control.closest('.t4s-product');
        const title = card && card.querySelector('.t4s-product-title');
        const name = title && title.textContent.trim();
        if (name) control.setAttribute('aria-label', 'View ' + name);
      } else if (control.matches('.is--minus')) {
        control.setAttribute('aria-label', 'Decrease quantity');
      } else if (control.matches('.is--plus')) {
        control.setAttribute('aria-label', 'Increase quantity');
      } else if (control.matches('.t4s-quantity-input')) {
        control.setAttribute('aria-label', 'Quantity');
      } else if (control.src && control.src.includes('merchantverse')) {
        control.setAttribute('title', 'Google Store quality badge');
      } else {
        control.setAttribute('title', 'Shopify theme preview controls');
      }
    });
  };

  const configureDrawers = (root = document) => {
    const labels = {
      't4s-mini_cart': 'Shopping cart',
      't4s-login-sidebar': 'Customer account',
      't4s-search-hidden': 'Search',
      't4s-menu-drawer': 'Main navigation'
    };
    Object.entries(labels).forEach(([id, label]) => {
      const drawer = root === document
        ? document.getElementById(id)
        : (root.id === id ? root : root.querySelector && root.querySelector('#' + id));
      if (drawer) {
        if (!drawer.hasAttribute('role')) drawer.setAttribute('role', 'dialog');
        if (!drawer.hasAttribute('aria-label')) drawer.setAttribute('aria-label', label);
        drawer.setAttribute('aria-modal', 'true');
      }
    });
  };

  const normalizeTelephoneLinks = (root = document) => {
    matchesWithin(root, 'a[href^="tel:"]').forEach((link) => {
      const rawValue = (link.getAttribute('href') || '').slice(4);
      const digits = rawValue.replace(/\D/g, '');
      let normalized = '';
      if (digits.length === 10) normalized = 'tel:+1' + digits;
      else if (digits.length === 11 && digits.charAt(0) === '1') normalized = 'tel:+' + digits;
      if (normalized && link.getAttribute('href') !== normalized) link.setAttribute('href', normalized);
    });
  };


  const normalizeFittingLinks = (root = document) => {
    matchesWithin(root, 'a[href]').forEach((link) => {
      const label = (link.textContent || '').replace(/\s+/g, ' ').trim();
      const ariaLabel = (link.getAttribute('aria-label') || '').replace(/\s+/g, ' ').trim();
      if (!/^(book|request|schedule)\b.*\bfitting\b/i.test(label + ' ' + ariaLabel)) return;
      link.setAttribute('href', '/pages/book-mattress-fitting');
      link.removeAttribute('target');
      link.removeAttribute('rel');
      if (/^call\b/i.test(ariaLabel)) link.setAttribute('aria-label', 'Book a mattress fitting');
    });
  };

  const labelPurposeLinks = (root = document) => {
    matchesWithin(root, '.tx-topbar-directions:not([aria-label])').forEach((link) => {
      const context = ((link.closest('.tx-topbar-actions') || link).textContent || '') + ' ' + (link.getAttribute('href') || '');
      let city = '';
      if (/magnolia/i.test(context)) city = 'Magnolia';
      else if (/huntsville/i.test(context)) city = 'Huntsville';
      else if (/montgomery/i.test(context)) city = 'Montgomery';
      link.setAttribute('aria-label', city ? 'Get directions to Texan Mattress ' + city : 'Get directions to Texan Mattress');
    });
    matchesWithin(root, 'a[href^="javascript:"]').forEach((link) => {
      const context = [link.className || '',link.getAttribute('aria-label') || '',link.textContent || '',(link.closest('[class*="affirm"], [data-affirm]') || {}).className || ''].join(' ');
      if (!/affirm|learn more/i.test(context)) return;
      link.removeAttribute('href');
      link.setAttribute('role', 'button');
      link.setAttribute('tabindex', '0');
      if (!link.getAttribute('aria-label')) link.setAttribute('aria-label', 'Learn more about Affirm financing');
      if (link.dataset.a11yButtonReady === 'true') return;
      link.dataset.a11yButtonReady = 'true';
      link.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        link.click();
      });
    });
  };

  const run = (root = document) => {
    setHiddenRegionState(root);
    labelDynamicControls(root);
    configureDrawers(root);
    normalizeTelephoneLinks(root);
    normalizeFittingLinks(root);
    labelPurposeLinks(root);
  };

  const boot = () => {
    run(document);

    const pendingRoots = new Set();
    const pendingRegions = new Set();
    let queued = false;

    const flush = () => {
      queued = false;
      pendingRegions.forEach(syncRegion);
      const roots = Array.from(pendingRoots).filter((root, index, allRoots) =>
        !allRoots.some((candidate, candidateIndex) => candidateIndex !== index && candidate.contains(root))
      );
      roots.forEach(run);
      pendingRegions.clear();
      pendingRoots.clear();
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes') {
          pendingRegions.add(mutation.target);
          return;
        }

        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          pendingRoots.add(node);
          const parentRegion = node.closest(regionSelector);
          if (parentRegion) pendingRegions.add(parentRegion);
        });
      });

      if (!queued && (pendingRoots.size || pendingRegions.size)) {
        queued = true;
        requestAnimationFrame(flush);
      }
    });

    observer.observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-hidden']
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
